import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  api,
  ApiError,
  type MissionProfilePayload,
  type MissionIntegration,
  type Mission,
  type ToolDefinition,
  type IntegrationDefinition,
  type ProcessDefinitionSummary,
  type SourceDefinition,
  type WorkflowStepStatus,
} from '@/api'
import { buildCompletedStepSet } from '@/lib/workflow/stepState'

export interface WorkflowStepDefinition {
  id: string
  label: string
  description?: string
}

const fallbackSteps: WorkflowStepDefinition[] = [
  { id: 'mission', label: 'Mission', description: 'Create and scope the mission.' },
  { id: 'profile', label: 'Mission Profile', description: 'Capture system and stakeholder details.' },
  { id: 'categorization', label: 'System Categorization', description: 'CNSSI 1253 CIA categorization.' },
  { id: 'stigs', label: 'STIGs', description: 'Select applicable STIGs.' },
  { id: 'sources', label: 'Sources', description: 'Ingest or reference source code.' },
  { id: 'integrations', label: 'Integrations', description: 'Validate external integrations.' },
  { id: 'tooling', label: 'Tooling', description: 'Select and configure tools.' },
  { id: 'scans', label: 'Run Scans', description: 'Execute scans and track progress.' },
  { id: 'review', label: 'Review', description: 'HITL vetting and triage.' },
  { id: 'scoring', label: 'Scoring', description: 'JFAC scoring pipeline.' },
  { id: 'report', label: 'Report', description: 'Generate report artifacts.' },
]

const EMPTY_MISSIONS: Mission[] = []
const EMPTY_PROCESSES: ProcessDefinitionSummary[] = []
const EMPTY_TOOLS: ToolDefinition[] = []
const EMPTY_INTEGRATIONS: IntegrationDefinition[] = []
const EMPTY_MISSION_INTEGRATIONS: MissionIntegration[] = []
const EMPTY_WORKFLOW_STEPS: WorkflowStepStatus[] = []
const EMPTY_SOURCES: SourceDefinition[] = []

export function useSoftwareAssuranceWorkflow() {
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null)
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null)
  const [selectedTools, setSelectedTools] = useState<Record<string, boolean>>({})
  const [targetPath, setTargetPath] = useState('')
  const [runLog, setRunLog] = useState<string[]>([])
  const [sourceForm, setSourceForm] = useState({
    source_type: 'repo',
    label: '',
    location: '',
    branch: '',
    commit: '',
    auth_type: '',
    accessToken: '',
  })
  const [sourceStatus, setSourceStatus] = useState<string | null>(null)
  const [activeToolConfigId, setActiveToolConfigId] = useState<string | null>(null)
  const [toolConfigDrafts, setToolConfigDrafts] = useState<Record<string, { endpoint_url: string; bearer_token: string }>>(
    {}
  )
  const [showRunDrawer, setShowRunDrawer] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)
  const [reportBusy, setReportBusy] = useState(false)
  const [profileStatus, setProfileStatus] = useState<string | null>(null)
  const [integrationConfigDrafts, setIntegrationConfigDrafts] = useState<Record<string, { system_id: string }>>({})
  const [integrationStatus, setIntegrationStatus] = useState<Record<string, string>>({})
  const [integrationValidation, setIntegrationValidation] = useState<Record<string, { validated: boolean; http_status?: number }>>({})

  const { data: missions = EMPTY_MISSIONS, isLoading: missionsLoading } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  const { data: processList } = useQuery({
    queryKey: ['metadata', 'processes', 'SoftwareIQ', 'active'],
    queryFn: () => api.metadata.processes.list({ module: 'SoftwareIQ', active_only: true }),
  })

  const { data: processDetail } = useQuery({
    queryKey: ['metadata', 'processes', selectedProcessId],
    queryFn: () => api.metadata.processes.get(selectedProcessId!),
    enabled: !!selectedProcessId,
  })

  const { data: tools = EMPTY_TOOLS } = useQuery({
    queryKey: ['softwareiq', 'tools'],
    queryFn: () => api.softwareiq.tools.list({ active_only: false }),
  })

  const { data: missionProfile, refetch: refetchMissionProfile, isFetching: missionProfileLoading } = useQuery({
    queryKey: ['softwareiq', 'mission-profile', selectedMissionId],
    queryFn: async () => {
      try {
        return await api.softwareiq.missionProfiles.get(selectedMissionId!)
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!selectedMissionId,
  })

  const { data: integrationsResponse } = useQuery({
    queryKey: ['metadata', 'integrations'],
    queryFn: () => api.metadata.integrations.list(),
  })

  const { data: missionIntegrationsResponse, refetch: refetchMissionIntegrations } = useQuery({
    queryKey: ['softwareiq', 'mission-integrations', selectedMissionId],
    queryFn: () => api.softwareiq.missionIntegrations.list(selectedMissionId!),
    enabled: !!selectedMissionId,
  })

  const { data: workflowStepsResponse, refetch: refetchWorkflowSteps } = useQuery({
    queryKey: ['softwareiq', 'workflow-steps', selectedMissionId],
    queryFn: () => api.softwareiq.workflowSteps.list(selectedMissionId!),
    enabled: !!selectedMissionId,
  })

  const { data: sourcesResponse, refetch: refetchSources } = useQuery({
    queryKey: ['softwareiq', 'sources', selectedMissionId],
    queryFn: () => api.softwareiq.sources.list({ mission_id: selectedMissionId! }),
    enabled: !!selectedMissionId,
  })

  const { data: toolRuns = [], refetch: refetchToolRuns } = useQuery({
    queryKey: ['softwareiq', 'tool-runs', selectedMissionId],
    queryFn: () => api.softwareiq.toolRuns.list({ mission_id: selectedMissionId! }),
    enabled: !!selectedMissionId,
    refetchInterval: (query) =>
      query.state.data?.some((run) => ['queued', 'running'].includes(run.status)) ? 5000 : false,
  })

  const { data: observationCounts } = useQuery({
    queryKey: ['softwareiq', 'observations', 'counts', selectedMissionId],
    queryFn: () => api.softwareiq.observations.getCounts(selectedMissionId!),
    enabled: !!selectedMissionId,
  })

  const { data: missionStats, refetch: refetchStats, isFetching: statsLoading } = useQuery({
    queryKey: ['softwareiq', 'stats', selectedMissionId],
    queryFn: () => api.softwareiq.stats.getMissionStats(selectedMissionId!),
    enabled: !!selectedMissionId,
  })

  const { data: reportCapabilities } = useQuery({
    queryKey: ['softwareiq', 'reports', 'capabilities'],
    queryFn: () => api.softwareiq.reports.getCapabilities(),
  })

  const processes = processList?.processes ?? EMPTY_PROCESSES
  const sources = sourcesResponse?.sources ?? EMPTY_SOURCES
  const integrations = integrationsResponse?.integrations ?? EMPTY_INTEGRATIONS
  const missionIntegrations = missionIntegrationsResponse?.integrations ?? EMPTY_MISSION_INTEGRATIONS
  const workflowSteps = workflowStepsResponse?.steps ?? EMPTY_WORKFLOW_STEPS
  const completedSteps = useMemo(() => buildCompletedStepSet(workflowSteps), [workflowSteps])

  const missionIntegrationMap = useMemo(() => {
    const map = new Map<string, MissionIntegration>()
    missionIntegrations.forEach((integration) => map.set(integration.integration_id, integration))
    return map
  }, [missionIntegrations])

  useEffect(() => {
    if (processes.length === 0) {
      return
    }
    if (!selectedProcessId) {
      const activeProcess = processes.find((process) => process.is_active) ?? processes[0]
      setSelectedProcessId(activeProcess.process_id)
    }
  }, [processes, selectedProcessId])

  useEffect(() => {
    setToolConfigDrafts((prev) => {
      const next = { ...prev }
      let changed = false
      tools.forEach((tool) => {
        if (!next[tool.tool_id]) {
          next[tool.tool_id] = {
            endpoint_url: tool.endpoint_url ?? '',
            bearer_token: '',
          }
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [tools])

  useEffect(() => {
    setIntegrationConfigDrafts((prev) => {
      const next = { ...prev }
      let changed = false
      integrations.forEach((integration) => {
        const binding = missionIntegrationMap.get(integration.integration_id)
        const bindingConfig = (binding?.config as Record<string, unknown>) ?? {}
        const bindingSystemId =
          typeof bindingConfig.system_id === 'string'
            ? bindingConfig.system_id
            : typeof bindingConfig.systemId === 'string'
            ? bindingConfig.systemId
            : ''
        const validationParams = (integration.validation as Record<string, unknown>)?.params as
          | Record<string, unknown>
          | undefined
        const defaultSystemId = typeof validationParams?.systemId === 'string' ? validationParams.systemId : ''
        const existingDraft = next[integration.integration_id]
        const nextSystemId = bindingSystemId || defaultSystemId || existingDraft?.system_id || ''
        if (!existingDraft || existingDraft.system_id !== nextSystemId) {
          next[integration.integration_id] = { system_id: nextSystemId }
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [integrations, missionIntegrationMap])

  const steps = useMemo(() => {
    if (processDetail?.definition && 'steps' in processDetail.definition) {
      const definition = processDetail.definition as { steps?: WorkflowStepDefinition[] }
      if (definition.steps && definition.steps.length > 0) {
        return definition.steps
      }
    }
    return fallbackSteps
  }, [processDetail])

  const current = steps[currentStep]

  const profileDefaults = useMemo(() => {
    if (!missionProfile) return undefined
    return {
      system_name: missionProfile.system_name,
      system_version: missionProfile.system_version,
      customer_org: missionProfile.customer_org,
      customer_poc_name: missionProfile.customer_poc_name,
      customer_poc_email: missionProfile.customer_poc_email,
      lead_analyst: missionProfile.lead_analyst,
      supporting_analysts: missionProfile.supporting_analysts,
      engagement_start: missionProfile.engagement_start,
      engagement_end: missionProfile.engagement_end,
      cia_confidentiality: missionProfile.cia_confidentiality,
      cia_integrity: missionProfile.cia_integrity,
      cia_availability: missionProfile.cia_availability,
      cia_rationale: missionProfile.cia_rationale,
      notes: missionProfile.notes,
    }
  }, [missionProfile])

  const toggleTool = (toolId: string) => {
    setSelectedTools((prev) => ({ ...prev, [toolId]: !prev[toolId] }))
  }

  const profileMutation = useMutation({
    mutationFn: (payload: MissionProfilePayload) =>
      api.softwareiq.missionProfiles.upsert(selectedMissionId!, payload),
    onSuccess: () => {
      setProfileStatus('Mission profile saved.')
      refetchMissionProfile()
    },
    onError: (error) => {
      setProfileStatus(error instanceof Error ? error.message : 'Mission profile save failed.')
    },
  })

  const handleProfileSubmit = async (data: Record<string, unknown>) => {
    if (!selectedMissionId) {
      setProfileStatus('Select a mission before saving a profile.')
      return
    }
    setProfileStatus(null)
    await profileMutation.mutateAsync(data as unknown as MissionProfilePayload)
  }

  const handleValidateIntegration = async (integrationId: string) => {
    try {
      const result = await api.metadata.integrations.validate(integrationId)
      setIntegrationValidation((prev) => ({
        ...prev,
        [integrationId]: { validated: result.validated, http_status: result.http_status },
      }))
      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationId]: result.validated ? 'Validation succeeded.' : 'Validation failed.',
      }))
    } catch (error) {
      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationId]: error instanceof Error ? error.message : 'Validation failed.',
      }))
    }
  }

  const handleBindIntegration = async (integrationId: string, integrationType: string) => {
    if (!selectedMissionId) {
      setIntegrationStatus((prev) => ({ ...prev, [integrationId]: 'Select a mission first.' }))
      return
    }
    const config = integrationConfigDrafts[integrationId]
    try {
      await api.softwareiq.missionIntegrations.upsert(selectedMissionId, integrationId, {
        integration_type: integrationType,
        status: 'linked',
        config: config?.system_id ? { system_id: config.system_id } : {},
      })
      setIntegrationStatus((prev) => ({ ...prev, [integrationId]: 'Integration linked.' }))
      refetchMissionIntegrations()
    } catch (error) {
      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationId]: error instanceof Error ? error.message : 'Integration link failed.',
      }))
    }
  }

  const handleRemoveIntegration = async (integrationId: string) => {
    if (!selectedMissionId) {
      setIntegrationStatus((prev) => ({ ...prev, [integrationId]: 'Select a mission first.' }))
      return
    }
    try {
      await api.softwareiq.missionIntegrations.remove(selectedMissionId, integrationId)
      setIntegrationStatus((prev) => ({ ...prev, [integrationId]: 'Integration removed.' }))
      refetchMissionIntegrations()
    } catch (error) {
      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationId]: error instanceof Error ? error.message : 'Integration removal failed.',
      }))
    }
  }

  const handleStepCompletion = async (stepId: string, completed: boolean) => {
    if (!selectedMissionId) {
      return
    }
    try {
      await api.softwareiq.workflowSteps.update(selectedMissionId, stepId, completed)
      refetchWorkflowSteps()
    } catch (error) {
      setRunLog((prev) => [
        ...prev,
        `Workflow step update failed (${error instanceof Error ? error.message : 'error'}).`,
      ])
    }
  }

  const handleAddSource = async () => {
    if (!selectedMissionId) {
      setSourceStatus('Select a mission before adding sources.')
      return
    }
    if (!sourceForm.location.trim()) {
      setSourceStatus('Provide a source location before saving.')
      return
    }
    setSourceStatus(null)
    try {
      await api.softwareiq.sources.create({
        mission_id: selectedMissionId,
        source_type: sourceForm.source_type as 'repo' | 'filesystem' | 'url',
        label: sourceForm.label || undefined,
        location: sourceForm.location,
        branch: sourceForm.branch || undefined,
        commit: sourceForm.commit || undefined,
        auth_type: sourceForm.auth_type || undefined,
        connection: sourceForm.accessToken ? { bearer_token: sourceForm.accessToken } : undefined,
      })
      setSourceForm((prev) => ({
        source_type: prev.source_type,
        label: '',
        location: '',
        branch: '',
        commit: '',
        auth_type: '',
        accessToken: '',
      }))
      setSourceStatus('Source registered.')
      refetchSources()
    } catch (error) {
      setSourceStatus(error instanceof Error ? error.message : 'Failed to register source.')
    }
  }

  const handleSaveToolConfig = async (toolId: string) => {
    const draft = toolConfigDrafts[toolId]
    if (!draft) return
    const payload: Record<string, unknown> = {}
    if (draft.endpoint_url) {
      payload.endpoint_url = draft.endpoint_url
    }
    if (draft.bearer_token) {
      payload.connection = { bearer_token: draft.bearer_token }
    }
    try {
      await api.softwareiq.tools.update(toolId, payload)
      setToolConfigDrafts((prev) => ({
        ...prev,
        [toolId]: { ...prev[toolId], bearer_token: '' },
      }))
      setActiveToolConfigId(null)
    } catch (error) {
      setRunLog((prev) => [
        ...prev,
        `Tool config save failed (${error instanceof Error ? error.message : 'error'}).`,
      ])
    }
  }

  const handleDownloadArtifact = async (runId: string, index: number, filename: string) => {
    try {
      const blob = await api.softwareiq.toolRuns.downloadArtifact(runId, index)
      api.softwareiq.reports.downloadBlob(blob, filename)
    } catch (error) {
      setRunLog((prev) => [
        ...prev,
        `Artifact download failed (${error instanceof Error ? error.message : 'error'}).`,
      ])
    }
  }

  const handleExportPdf = async () => {
    if (!selectedMissionId) {
      setReportError('Select a mission before exporting.')
      return
    }
    setReportBusy(true)
    setReportError(null)
    try {
      const blob = await api.softwareiq.reports.exportPdf(selectedMissionId)
      api.softwareiq.reports.downloadBlob(blob, `softwareiq-report-${selectedMissionId.slice(0, 8)}.pdf`)
    } catch (error) {
      setReportError(error instanceof Error ? error.message : 'PDF export failed.')
    } finally {
      setReportBusy(false)
    }
  }

  const handleExportCsv = async () => {
    if (!selectedMissionId) {
      setReportError('Select a mission before exporting.')
      return
    }
    setReportBusy(true)
    setReportError(null)
    try {
      const blob = await api.softwareiq.reports.exportCsv(selectedMissionId)
      api.softwareiq.reports.downloadBlob(blob, `softwareiq-findings-${selectedMissionId.slice(0, 8)}.csv`)
    } catch (error) {
      setReportError(error instanceof Error ? error.message : 'CSV export failed.')
    } finally {
      setReportBusy(false)
    }
  }

  const handleExportHtml = async () => {
    if (!selectedMissionId) {
      setReportError('Select a mission before exporting.')
      return
    }
    setReportBusy(true)
    setReportError(null)
    try {
      const html = await api.softwareiq.reports.exportHtml(selectedMissionId)
      const reportWindow = window.open('', '_blank')
      if (reportWindow) {
        reportWindow.document.open()
        reportWindow.document.write(html)
        reportWindow.document.close()
      }
    } catch (error) {
      setReportError(error instanceof Error ? error.message : 'HTML export failed.')
    } finally {
      setReportBusy(false)
    }
  }

  const handleRunTools = async () => {
    if (!selectedMissionId) {
      setRunLog((prev) => [...prev, 'Select a mission before running tools.'])
      return
    }
    const runTargets = tools.filter((tool) => selectedTools[tool.tool_id])
    if (runTargets.length === 0) {
      setRunLog((prev) => [...prev, 'Select at least one tool to execute.'])
      return
    }

    for (const tool of runTargets) {
      if (!tool.endpoint_url) {
        setRunLog((prev) => [...prev, `${tool.name}: missing endpoint configuration.`])
        continue
      }
      try {
        const response = await api.softwareiq.tools.run(tool.tool_id, {
          mission_id: selectedMissionId,
          target: { target_type: 'source', path: targetPath },
          config: { profile: 'default', parameters: {} },
        })
        setRunLog((prev) => [...prev, `${tool.name}: run accepted (${response.run_id}).`])
      } catch (error) {
        setRunLog((prev) => [
          ...prev,
          `${tool.name}: failed to start (${error instanceof Error ? error.message : 'error'}).`,
        ])
      }
    }
  }

  const getRunArtifacts = (run: { run_metadata?: Record<string, unknown> }) => {
    const metadata = (run.run_metadata ?? {}) as Record<string, unknown>
    const directArtifacts = Array.isArray(metadata.artifacts) ? metadata.artifacts : []
    const result = metadata.result as Record<string, unknown> | undefined
    const nestedArtifacts = Array.isArray(result?.artifacts) ? result?.artifacts : []
    const artifacts = directArtifacts.length > 0 ? directArtifacts : nestedArtifacts
    return artifacts
      .map((entry, index) => {
        if (typeof entry === 'string') {
          return { index, filename: entry.split('/').pop() || `artifact-${index}` }
        }
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>
          const path = (record.path as string) || (record.location as string) || ''
          return { index, filename: (record.filename as string) || path.split('/').pop() || `artifact-${index}` }
        }
        return null
      })
      .filter((entry): entry is { index: number; filename: string } => !!entry)
  }

  return {
    currentStep,
    setCurrentStep,
    selectedMissionId,
    setSelectedMissionId,
    selectedProcessId,
    setSelectedProcessId,
    selectedTools,
    targetPath,
    setTargetPath,
    runLog,
    sourceForm,
    setSourceForm,
    sourceStatus,
    activeToolConfigId,
    setActiveToolConfigId,
    toolConfigDrafts,
    setToolConfigDrafts,
    showRunDrawer,
    setShowRunDrawer,
    reportError,
    reportBusy,
    profileStatus,
    integrationConfigDrafts,
    setIntegrationConfigDrafts,
    integrationStatus,
    integrationValidation,
    missions,
    missionsLoading,
    processes,
    tools,
    missionProfile,
    missionProfileLoading,
    integrations,
    missionIntegrations,
    missionIntegrationMap,
    workflowSteps,
    completedSteps,
    sources,
    toolRuns,
    observationCounts,
    missionStats,
    statsLoading,
    reportCapabilities,
    steps,
    current,
    profileDefaults,
    profileMutationPending: profileMutation.isPending,
    toggleTool,
    handleProfileSubmit,
    handleValidateIntegration,
    handleBindIntegration,
    handleRemoveIntegration,
    handleStepCompletion,
    handleAddSource,
    handleSaveToolConfig,
    handleDownloadArtifact,
    handleExportPdf,
    handleExportCsv,
    handleExportHtml,
    handleRunTools,
    getRunArtifacts,
    refetchToolRuns,
    refetchStats,
  }
}

export type SoftwareAssuranceWorkflowController = ReturnType<typeof useSoftwareAssuranceWorkflow>
